import { Room } from '@spike/room-model';
import { Task } from '@spike/task-model';

export const NO_ROOM_KEY = 'room:none';

export interface UnitGroup {
    key: string;
    unitId: number;
    unitName: string;
    tasks: Array<Task>;
}

export interface RoomGroup {
    key: string;
    roomId: number | null;
    roomName: string;
    units: Array<UnitGroup>;
    tasks: Array<Task>;
    count: number;
}

export const taskRoomUnitId = (task: Task): number | undefined =>
    task.feeding?.roomUnitId ?? task.medication?.roomUnitId ?? task.addon?.roomUnitId;

export interface UnitRef {
    roomId: number;
    roomName: string;
    roomOrder: number;
    unitId: number;
    unitName: string;
    unitOrder: number;
}

export const buildUnitIndex = (rooms: Array<Room>): Map<number, UnitRef> => {
    const index = new Map<number, UnitRef>();
    rooms.forEach((room, roomOrder) => {
        if (room.id == null) {
            return;
        }
        room.units.forEach((unit, unitOrder) => {
            if (unit.id == null) {
                return;
            }
            index.set(unit.id, {
                roomId: room.id!,
                roomName: room.name,
                roomOrder,
                unitId: unit.id,
                unitName: unit.name,
                unitOrder
            });
        });
    });
    return index;
};

export const groupTasksByRoomUnit = (tasks: Array<Task>, rooms: Array<Room>): Array<RoomGroup> => {
    const index = buildUnitIndex(rooms);

    const noRoom: RoomGroup = {
        key: NO_ROOM_KEY,
        roomId: null,
        roomName: 'No Room Assigned',
        units: [],
        tasks: [],
        count: 0
    };

    type RoomBucket = RoomGroup & { order: number; unitOrder: Map<number, number> };
    const roomMap = new Map<number, RoomBucket>();

    tasks.forEach(task => {
        const unitId = taskRoomUnitId(task);
        const ref = unitId != null ? index.get(unitId) : undefined;

        if (!ref) {
            noRoom.tasks.push(task);
            noRoom.count++;
            return;
        }

        let room = roomMap.get(ref.roomId);
        if (!room) {
            room = {
                key: `room:${ref.roomId}`,
                roomId: ref.roomId,
                roomName: ref.roomName,
                units: [],
                tasks: [],
                count: 0,
                order: ref.roomOrder,
                unitOrder: new Map()
            };
            roomMap.set(ref.roomId, room);
        }

        let unit = room.units.find(candidate => candidate.unitId === ref.unitId);
        if (!unit) {
            unit = { key: `unit:${ref.roomId}:${ref.unitId}`, unitId: ref.unitId, unitName: ref.unitName, tasks: [] };
            room.units.push(unit);
            room.unitOrder.set(ref.unitId, ref.unitOrder);
        }
        unit.tasks.push(task);
        room.count++;
    });

    const orderedRooms = Array.from(roomMap.values())
        .sort((a, b) => a.order - b.order)
        .map(room => {
            room.units.sort((a, b) => (room.unitOrder.get(a.unitId) ?? 0) - (room.unitOrder.get(b.unitId) ?? 0));
            const { order, unitOrder, ...group } = room;
            return group as RoomGroup;
        });

    return noRoom.count > 0 ? [noRoom, ...orderedRooms] : orderedRooms;
};

export const roomGroupTasks = (group: RoomGroup): Array<Task> =>
    group.tasks.concat(group.units.flatMap(unit => unit.tasks));

export const incompleteTaskIds = (tasks: Array<Task>): Array<number> =>
    tasks.filter(task => task.status !== 'completed' && task.id != null).map(task => task.id!);
